/**
 * iSolved GL Report parser
 *
 * Detects the tabular source sheet (e.g. Sheet1 with Date/Unit/Account/Amount/Desc)
 * and extracts keyword metadata (Company ID, Comments, etc.) from any sheet.
 *
 * Also reads workbooks that are already in GL8610XL keyword format (a `Layout`
 * row followed by `Detail` rows, e.g. the hand-built FINAL sheet a payroll JE
 * gets staged in) — see findKeywordDetailSheet().
 */
import * as XLSX from 'xlsx';
import { coerceNumber } from './ingest';

const HEADER_ALIASES = {
  date: ['date', 'accounting_date', 'pay_date', 'pay_period'],
  unit: ['unit', 'unit_id', 'business_unit', 'bu'],
  account: ['account', 'account_no', 'account_number', 'acct'],
  amount: ['amount', 'debit', 'net_amount'],
  description: ['desc', 'description', 'remarks'],
};

const PREFERRED_SHEET_NAMES = ['sheet1', 'data', 'gl report', 'gl_report', 'export', 'detail'];
const AVOID_SHEET_NAMES = ['final', 'mv', 'import', 'output'];

function normalizeHeader(key) {
  return String(key).trim().toLowerCase().replace(/\s+/g, '_');
}

function matchHeader(headers, aliases) {
  return headers.find((h) => aliases.includes(h));
}

function analyzeSheet(sheet, sheetName) {
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const headerRow = rawRows[0] ?? [];
  const headers = headerRow.map(normalizeHeader).filter(Boolean);

  const mapping = {
    date: matchHeader(headers, HEADER_ALIASES.date),
    unit: matchHeader(headers, HEADER_ALIASES.unit),
    account: matchHeader(headers, HEADER_ALIASES.account),
    amount: matchHeader(headers, HEADER_ALIASES.amount),
    description: matchHeader(headers, HEADER_ALIASES.description),
  };

  const hasTabular = mapping.unit && mapping.account && mapping.amount;
  const dataRowCount = hasTabular ? rawRows.length - 1 : 0;

  let score = 0;
  if (hasTabular) score += 50 + Math.min(dataRowCount, 100);
  const lowerName = sheetName.toLowerCase();
  if (PREFERRED_SHEET_NAMES.some((n) => lowerName.includes(n))) score += 20;
  if (AVOID_SHEET_NAMES.some((n) => lowerName === n || lowerName.includes(n))) score -= 30;

  return { sheetName, mapping, hasTabular, dataRowCount, score, headers };
}

function parseRows(sheet, mapping) {
  const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return raw.map((row) => {
    const normalized = {};
    for (const [key, val] of Object.entries(row)) {
      normalized[normalizeHeader(key)] = val;
    }
    return {
      date: normalized[mapping.date] ?? '',
      unit: normalized[mapping.unit] ?? '',
      account: normalized[mapping.account] ?? '',
      amount: normalized[mapping.amount] ?? '',
      description: String(normalized[mapping.description] ?? '').trim(),
    };
  }).filter((row) => row.unit !== '' || row.account !== '');
}

export function extractKeywordMeta(sourceWb, defaults = {}) {
  const meta = {
    companyId: '',
    accountingDate: '',
    entryStatus: 'O',
    remarks: 'Payroll Entry',
    dateMask: '',
    entryType: 'JE',
    ...defaults,
  };

  for (const sheetName of sourceWb.SheetNames) {
    const raw = XLSX.utils.sheet_to_json(sourceWb.Sheets[sheetName], { header: 1, defval: '' });
    for (const row of raw.slice(0, 30)) {
      const keyword = String(row[0] ?? '').trim().toLowerCase();
      const value = row[1];
      if (!value && value !== 0) continue;

      if (keyword === 'company id') meta.companyId = String(value).trim();
      if (keyword === 'comments') meta.remarks = String(value).trim();
      if (keyword === 'entry status') meta.entryStatus = String(value).trim();
      if (keyword === 'entry type') meta.entryType = String(value).trim().toUpperCase();
      if (keyword === 'date mask') meta.dateMask = String(value).trim().toLowerCase();
      if (keyword === 'date' || keyword === 'accounting date') {
        const parsed = formatAccountingDate(value, meta.dateMask);
        if (parsed) meta.accountingDate = parsed;
      }
    }
  }

  return meta;
}

// Layout-row header → canonical detail field, for keyword-format workbooks.
const LAYOUT_ALIASES = {
  unit: ['unit', 'unit_id', 'business_unit', 'bu'],
  account: ['account', 'account_no', 'account_number', 'acct'],
  amount: ['amount', 'amt', 'debit'],
  description: ['remarks', 'remark', 'description', 'desc', 'comment', 'comments'],
  project_type: ['project_type', 'projtype', 'proj_type'],
  project_id: ['project_id', 'projid', 'proj_id', 'project'],
};

// Column A of a Layout row maps the columns of every Detail row beneath it.
// Returns canonical field → column index, or null when unit/account/amount
// aren't all present (i.e. not a usable detail layout).
function resolveLayoutRow(row) {
  const mapping = {};
  row.forEach((cell, index) => {
    if (index === 0) return; // column A holds the keyword itself
    const norm = normalizeHeader(cell);
    if (!norm) return;
    for (const [canonical, aliases] of Object.entries(LAYOUT_ALIASES)) {
      if (mapping[canonical] === undefined && aliases.includes(norm)) mapping[canonical] = index;
    }
  });
  const complete = ['unit', 'account', 'amount'].every((f) => mapping[f] !== undefined);
  return complete ? mapping : null;
}

/**
 * Read a workbook that is already in GL8610XL keyword format: keyword rows in
 * column A, a `Layout` row naming the detail columns, then `Detail` rows.
 *
 * Header keywords (Company ID, Date, Entry Status, Comments…) are NOT read here
 * — extractKeywordMeta() handles those. A re-declared Layout row re-maps the
 * columns beneath it; multiple header blocks with *different* metadata in one
 * sheet are not split apart (all Detail rows are returned as one set).
 */
export function findKeywordDetailSheet(sourceWb) {
  for (const sheetName of sourceWb.SheetNames) {
    const raw = XLSX.utils.sheet_to_json(sourceWb.Sheets[sheetName], { header: 1, defval: '' });

    let mapping = null;
    const rows = [];
    for (const row of raw) {
      const keyword = String(row?.[0] ?? '').trim().toLowerCase();
      if (keyword === 'layout') {
        mapping = resolveLayoutRow(row) ?? mapping;
        continue;
      }
      if (keyword !== 'detail' || !mapping) continue;

      const pick = (field) => (mapping[field] === undefined ? '' : row[mapping[field]]);
      const account = String(pick('account') ?? '').trim();
      const unit = String(pick('unit') ?? '').trim();
      if (!unit && !account) continue;

      rows.push({
        date: '',
        unit,
        account,
        // null, not 0, when there's no usable amount — the $0 filter and the
        // validator rely on telling those two apart (see src/lib/amounts.js).
        amount: coerceNumber(pick('amount')),
        description: String(pick('description') ?? '').trim(),
        project_type: String(pick('project_type') ?? '').trim(),
        project_id: String(pick('project_id') ?? '').trim(),
      });
    }

    if (rows.length > 0) return { sheetName, rows, mapping, format: 'keyword' };
  }
  return null;
}

export function findISolvedSourceSheet(sourceWb) {
  const candidates = sourceWb.SheetNames
    .map((name) => analyzeSheet(sourceWb.Sheets[name], name))
    .filter((c) => c.hasTabular && c.dataRowCount > 0)
    .sort((a, b) => b.score - a.score);

  if (candidates.length === 0) return null;

  const best = candidates[0];
  const sheet = sourceWb.Sheets[best.sheetName];
  const rows = parseRows(sheet, best.mapping);

  return {
    sheetName: best.sheetName,
    rows,
    mapping: best.mapping,
  };
}

export function formatAccountingDate(value, dateMask = '') {
  if (value instanceof Date && !isNaN(value.getTime())) {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  }

  const s = String(value).trim();
  if (/^\d{8}$/.test(s)) {
    // MMDDYYYY (iSolved FINAL sheet with mmddyyyy mask)
    if (dateMask === 'mmddyyyy' || parseInt(s.slice(0, 2), 10) <= 12) {
      const mm = s.slice(0, 2);
      const dd = s.slice(2, 4);
      const yyyy = s.slice(4, 8);
      if (parseInt(mm, 10) >= 1 && parseInt(mm, 10) <= 12) {
        return `${yyyy}${mm}${dd}`;
      }
    }
    // YYYYMMDD
    return s;
  }

  // ISO date string
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return formatAccountingDate(d);
  }

  return '';
}

export function parseISolvedGLReport(sourceWb) {
  const keywordMeta = extractKeywordMeta(sourceWb);
  // Prefer the raw tabular export; fall back to a workbook already staged in
  // GL8610XL keyword format (Layout + Detail rows, no tabular Sheet1).
  const source = findISolvedSourceSheet(sourceWb) ?? findKeywordDetailSheet(sourceWb);

  if (!source) {
    throw new Error(
      'Could not find iSolved GL Report data. Expected a sheet with Date, Unit, Account, Amount ' +
        'columns, or a GL8610XL keyword sheet with a Layout row and Detail rows.'
    );
  }

  const rows = source.rows.map((row) => {
    const acctDate = formatAccountingDate(row.date, keywordMeta.dateMask) || keywordMeta.accountingDate;
    return {
      unit: String(row.unit).trim().padStart(3, '0'),
      account: String(row.account).trim(),
      // null (not 0) when unparseable, so a blank or mis-mapped amount column
      // is flagged by the validator instead of being filtered out as a $0 line.
      amount: coerceNumber(row.amount),
      description: row.description,
      accounting_date: acctDate,
      project_type: String(row.project_type ?? '').trim(),
      project_id: String(row.project_id ?? '').trim(),
    };
  });

  return {
    rows,
    meta: {
      ...keywordMeta,
      sourceSheet: source.sheetName,
      sourceFormat: source.format ?? 'tabular',
      rowCount: rows.length,
    },
  };
}

/**
 * Score a workbook for auto-matching the payroll profile.
 */
export function scorePayrollFile(sourceWb) {
  const source = findISolvedSourceSheet(sourceWb) ?? findKeywordDetailSheet(sourceWb);
  if (!source) return 0;

  let score = 40 + Math.min(source.rows.length, 50);
  const keywordMeta = extractKeywordMeta(sourceWb);
  if (keywordMeta.companyId) score += 10;
  if (keywordMeta.accountingDate) score += 10;

  const nameHint = sourceWb.SheetNames.some((n) =>
    /gl.?report|payroll|isolved/i.test(n)
  );
  if (nameHint) score += 15;

  return score;
}
